import { useState, useEffect } from 'react';
import { updateApp } from '../lib/api';
import { useAppConfigs } from '../hooks/useAppConfigs';
import type { AppStatus } from '../types';
import styles from './AppsConfigPage.module.css';

const STATUS_CONFIG: Record<AppStatus, { label: string; description: string }> = {
  enabled:  { label: 'Activo',    description: 'Tickets, Pame y Neo disponibles' },
  suspended: { label: 'Suspendido', description: 'Solo tickets — sin Pame ni Neo' },
  disabled:  { label: 'Inactivo',  description: 'Sin tickets, Pame ni Neo' },
};

export function AppsConfigPage() {
  const { apps, loading, refetch } = useAppConfigs();
  const [localColors,    setLocalColors]    = useState<Record<string, string>>({});
  const [localLabels,    setLocalLabels]    = useState<Record<string, string>>({});
  const [localPrefixes,  setLocalPrefixes]  = useState<Record<string, string>>({});
  const [localStatuses,  setLocalStatuses]  = useState<Record<string, AppStatus>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [saved,  setSaved]  = useState<string | null>(null);
  const [error,  setError]  = useState<string | null>(null);

  useEffect(() => {
    if (!apps.length) return;
    // Sincronizar local state desde el servidor. Esto ocurre solo en la carga inicial
    // y después de un save exitoso (refetch), por lo que no pisamos edits del usuario
    // en mitad de una sesión de edición.
    setLocalColors(Object.fromEntries(apps.map(a => [a.id, a.color])));
    setLocalLabels(Object.fromEntries(apps.map(a => [a.id, a.label])));
    setLocalPrefixes(Object.fromEntries(apps.map(a => [a.id, a.prefix ?? ''])));
    setLocalStatuses(Object.fromEntries(apps.map(a => [a.id, (a.status ?? 'enabled') as AppStatus])));
  }, [apps]);

  const isDirty = (appId: string) => {
    const app = apps.find(a => a.id === appId);
    if (!app) return false;
    return localColors[appId]   !== app.color
      || localLabels[appId]     !== app.label
      || localPrefixes[appId]   !== (app.prefix ?? '')
      || localStatuses[appId]   !== (app.status ?? 'enabled');
  };

  const handleSave = async (appId: string) => {
    const prefix = localPrefixes[appId]?.trim().toUpperCase();
    if (prefix && !/^[A-Z0-9]{1,6}$/.test(prefix)) {
      setError('El prefijo debe ser 1-6 letras o números (ej: OSK, XB, NAL)');
      return;
    }
    if (!localLabels[appId]?.trim()) {
      setError('El nombre no puede estar vacío');
      return;
    }
    setError(null);
    setSaving(appId);
    try {
      await updateApp(appId, {
        color:  localColors[appId],
        label:  localLabels[appId].trim(),
        prefix: prefix || undefined,
        status: localStatuses[appId],
      });
      setLocalColors(prev   => { const n = { ...prev }; delete n[appId]; return n; });
      setLocalLabels(prev   => { const n = { ...prev }; delete n[appId]; return n; });
      setLocalPrefixes(prev => { const n = { ...prev }; delete n[appId]; return n; });
      setLocalStatuses(prev => { const n = { ...prev }; delete n[appId]; return n; });
      await refetch();
      setSaved(appId);
      setTimeout(() => setSaved(null), 2000);
    } catch {
      setError('Error al guardar');
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className={styles.loading}>Cargando...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.title}>Aplicaciones</h2>
        <p className={styles.subtitle}>Nombre, prefijo, color y estado operativo por producto</p>
      </div>

      {error && (
        <div className={styles.errorBanner} role="alert">
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
          </svg>
          {error}
        </div>
      )}

      <div className={styles.grid}>
        {apps.map(app => {
          const color  = localColors[app.id]   ?? app.color;
          const label  = localLabels[app.id]   ?? app.label;
          const prefix = localPrefixes[app.id] ?? app.prefix ?? '';
          const status = localStatuses[app.id] ?? (app.status ?? 'enabled');
          const dirty  = isDirty(app.id);

          return (
            <div
              key={app.id}
              className={styles.card}
              style={{ '--app-color': color } as React.CSSProperties}
            >
              {/* Accent strip */}
              <div className={styles.accent} />

              {/* Card body */}
              <div className={styles.body}>

                {/* Top row: identity + status badge */}
                <div className={styles.topRow}>
                  <div className={styles.identity}>
                    <div className={styles.colorDot} style={{ background: color }} />
                    <div>
                      <div className={styles.appName}>{label}</div>
                      <div className={styles.appId}>{app.id}</div>
                    </div>
                  </div>

                  {/* 3-state segmented control */}
                  <div
                    className={styles.segmented}
                    role="group"
                    aria-label={`Estado de ${label}`}
                  >
                    {(Object.keys(STATUS_CONFIG) as AppStatus[]).map(s => (
                      <button
                        key={s}
                        className={`${styles.segBtn} ${status === s ? `${styles.segBtnActive} ${styles[`segBtnActive_${s}`]}` : ''}`}
                        onClick={() => setLocalStatuses(prev => ({ ...prev, [app.id]: s }))}
                        aria-pressed={status === s}
                        title={STATUS_CONFIG[s].description}
                        type="button"
                      >
                        {STATUS_CONFIG[s].label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Status description */}
                <div className={`${styles.statusDesc} ${styles[`statusDesc_${status}`]}`}>
                  {STATUS_CONFIG[status].description}
                </div>

                <div className={styles.divider} />

                {/* Fields */}
                <div className={styles.fields}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`label-${app.id}`}>Nombre</label>
                    <input
                      id={`label-${app.id}`}
                      className={styles.fieldInput}
                      value={label}
                      onChange={e => setLocalLabels(prev => ({ ...prev, [app.id]: e.target.value }))}
                      placeholder="Nombre de la app"
                    />
                  </div>

                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`prefix-${app.id}`}>Prefijo tickets</label>
                    <div className={styles.prefixRow}>
                      <input
                        id={`prefix-${app.id}`}
                        className={styles.prefixInput}
                        value={prefix}
                        maxLength={6}
                        onChange={e => setLocalPrefixes(prev => ({ ...prev, [app.id]: e.target.value.toUpperCase() }))}
                        placeholder="OSK"
                        aria-label={`Prefijo de tickets para ${label}`}
                      />
                      <span className={styles.prefixBadge} style={{ borderColor: color, color }}>
                        {prefix || '???'}-42
                      </span>
                    </div>
                  </div>

                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor={`color-${app.id}`}>Color</label>
                    <div className={styles.colorRow}>
                      <input
                        id={`color-${app.id}`}
                        type="color"
                        className={styles.colorPicker}
                        value={color}
                        onChange={e => setLocalColors(prev => ({ ...prev, [app.id]: e.target.value }))}
                        aria-label={`Color para ${label}`}
                      />
                      <span className={styles.colorHex}>{color.toUpperCase()}</span>
                      <div className={styles.colorSwatch} style={{ background: color }} />
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                  {saved === app.id && (
                    <span className={styles.savedMsg}>
                      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      Guardado
                    </span>
                  )}
                  <button
                    className={`${styles.saveBtn} ${dirty ? styles.saveBtnDirty : ''} ${saved === app.id ? styles.saveBtnOk : ''}`}
                    onClick={() => handleSave(app.id)}
                    disabled={saving === app.id || !dirty}
                    aria-label={`Guardar cambios en ${label}`}
                    type="button"
                  >
                    {saving === app.id ? (
                      <span className={styles.spinner} aria-hidden="true" />
                    ) : (
                      <>
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z"/>
                        </svg>
                        Guardar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
